'use client'

import * as React from 'react'
import { Decimal } from 'decimal.js'

import {
  OrderFieldDivider,
  OrderFieldStepper,
} from '@/features/order/order-form/components/widget/order-field-stepper'
import { useAdvancePresetTourOnQuantity } from '@/hooks/use-advance-preset-tour-on-quantity'
import { useFocusOnMount } from '@/hooks/use-focus-on-mount'
import { useNumberInput } from '@/hooks/use-number-format'
import { cn, safeParseFloat } from '@/lib/utils'
import { PRESET_TOUR_TARGET } from '@/state/preset-tour'

interface InputWithContractProps {
  value: string
  onValueChange: (value: string) => void
  targetTicker: string // e.g., "BTC", "ETH"
  contractSize?: number // e.g., 10000 for TRX. When > 1, input is in contracts.
  placeholder?: string
  className?: string
  disabled?: boolean
  // Shows the value but refuses edits (typing and the stepper); `title` explains why.
  readOnly?: boolean
  title?: string
  autoFocus?: boolean
  showNumberPad?: boolean
  step?: number
  amountInputIndex?: number
}

/**
 * Simple input component for option contracts
 * Displays: [input] [contract] [+/-]
 * With helper text below: "1 Contract = 1 BTC"
 * No ticker conversion - just a single unit display
 */
export function InputWithContract({
  value,
  onValueChange,
  targetTicker,
  contractSize,
  placeholder = '0',
  className,
  disabled = false,
  readOnly = false,
  title,
  autoFocus = false,
  showNumberPad = false,
  step = 1,
  amountInputIndex,
}: InputWithContractProps) {
  useAdvancePresetTourOnQuantity(value)
  const focusOnMountRef = useFocusOnMount<HTMLInputElement>(autoFocus)

  const isMultiContract = contractSize !== undefined && contractSize > 1
  const rawAmount = isMultiContract ? (safeParseFloat(value) || 0) * contractSize! : 0
  const currentValue = safeParseFloat(value) || 0
  const numberInput = useNumberInput(value)
  const isMinStep = currentValue <= step

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Typed in the user's separator style; refused keystrokes (ambiguous marks) come back null.
    const val = numberInput.parse(e.target.value)
    if (val !== null) {
      onValueChange(val)
    }
  }

  const handleIncrement = () => {
    const newValue = new Decimal(currentValue).plus(new Decimal(step))
    onValueChange(newValue.toFixed())
  }

  const handleDecrement = () => {
    if (isMinStep) return
    const newValue = Decimal.max(
      new Decimal(step),
      new Decimal(currentValue).minus(new Decimal(step))
    )
    onValueChange(newValue.toFixed())
  }

  return (
    <div
      title={title}
      className={cn(
        // Same shape as the limit price field: [amount | unit | +/−]. A container: see
        // InputWithTickerList — a narrow field slims the unit box instead of the number.
        'border-foreground/10 focus-within:border-foreground/20 @container relative flex h-9 items-center overflow-hidden border bg-transparent transition-colors',
        disabled && 'opacity-50',
        className
      )}
    >
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        data-1p-ignore
        data-preset-tour-target={PRESET_TOUR_TARGET.quantity}
        data-order-amount-index={amountInputIndex}
        value={numberInput.display}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        ref={focusOnMountRef}
        className="text-foreground placeholder:text-foreground/20 h-full min-w-0 flex-1 bg-transparent px-3 text-right text-sm font-medium outline-none"
      />
      <OrderFieldDivider />
      <div className="text-foreground/60 flex h-full min-w-18.5 shrink-0 items-center justify-center px-3 text-sm font-medium @max-[11rem]:min-w-0 @max-[11rem]:px-1.5 @max-[11rem]:text-xs">
        <span className="whitespace-nowrap">{targetTicker}</span>
      </div>
      {showNumberPad && (
        <>
          <OrderFieldDivider />
          <OrderFieldStepper
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            disabled={disabled || readOnly}
            decrementDisabled={isMinStep}
          />
        </>
      )}
      {isMultiContract && (
        <p className="text-foreground/30 absolute top-full right-0 pt-0.5 text-[10px]">
          = {rawAmount.toLocaleString('en-US')} {targetTicker}
        </p>
      )}
    </div>
  )
}
